import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

type SidebarDrillApi = {
  /** Re-open a sidebar drill panel (e.g. after drill-back while still in that section). */
  ensureDrillPanel: (panelId: string) => void;
  registerDrillHandler: (handler: (panelId: string) => void) => () => void;
};

const SidebarDrillContext = createContext<SidebarDrillApi | null>(null);

export function SidebarDrillProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<(panelId: string) => void>(() => {});

  const registerDrillHandler = useCallback((handler: (panelId: string) => void) => {
    handlerRef.current = handler;
    return () => {
      handlerRef.current = () => {};
    };
  }, []);

  const ensureDrillPanel = useCallback((panelId: string) => {
    handlerRef.current(panelId);
  }, []);

  const value = useMemo(
    () => ({ ensureDrillPanel, registerDrillHandler }),
    [ensureDrillPanel, registerDrillHandler]
  );

  return <SidebarDrillContext.Provider value={value}>{children}</SidebarDrillContext.Provider>;
}

export function useSidebarDrill(): SidebarDrillApi | null {
  return useContext(SidebarDrillContext);
}
